/**
 * Preparación de archivos antes de subirlos (2-10-2026).
 *
 * Por qué: la subida web pasa por una Lambda síncrona con límite de 6 MB EN BASE64 (+ cabeceras multipart),
 * es decir ~4,4 MB de archivo real. La interfaz decía 25 MB y una foto de móvil de 5,5 MB devolvía 413: una alta
 * real de Alemania subió bien un DNI de 3,5 MB, falló dos veces con la siguiente foto y se fue.
 * Una foto de 12 megapíxeles se puede bajar a 2.600 px de lado largo sin perder nada que el OCR lea (A4 a ~220 ppp),
 * y además sube mucho más rápido por datos móviles.
 *
 *   prepareFile(file) → { file, resized: { from_kb, to_kb } | null }   (nunca lanza: ante cualquier fallo devuelve el original)
 *   MAX_RAW_BYTES      → tamaño de archivo a partir del cual el servidor responde 413 (PDF u otros no comprimibles)
 */
export const MAX_RAW_BYTES = 4.3 * 1024 * 1024;
const RESIZE_ABOVE = 2.5 * 1024 * 1024; // por encima de esto se recomprime la imagen
const MAX_SIDE = 2600;
const TARGET_BYTES = 3 * 1024 * 1024;

const toBlob = (canvas, type, quality) => new Promise((resolve) => canvas.toBlob(resolve, type, quality));

// Textract solo lee JPEG, PNG, TIFF y PDF: WebP, GIF, BMP o AVIF se pasan a JPEG aunque sean pequeños (5-10-2026: un .webp
// llegó a la demo y falló sin avisar). Si el navegador no puede decodificarlo (HEIC fuera de Safari) se sube tal cual y el servidor
// responde con un mensaje claro.
const NATIVE = ["image/jpeg", "image/jpg", "image/pjpeg", "image/png", "image/tiff"];

export async function prepareFile(file) {
  try {
    const isImage = Boolean(file) && String(file.type || "").startsWith("image/");
    const unsupported = isImage && !NATIVE.includes(String(file.type).toLowerCase());
    if (!isImage || (file.size <= RESIZE_ABOVE && !unsupported)) return { file, resized: null };
    if (typeof createImageBitmap !== "function" || typeof document === "undefined") return { file, resized: null };

    // `from-image` respeta la orientación EXIF: las fotos de móvil salen derechas.
    const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff"; // los PNG con transparencia no se quedan negros en JPEG
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    if (typeof bitmap.close === "function") bitmap.close();

    let blob = null;
    for (const q of [0.85, 0.75, 0.65]) {
      blob = await toBlob(canvas, "image/jpeg", q);
      if (blob && blob.size <= TARGET_BYTES) break;
    }
    if (!blob || (blob.size >= file.size && !unsupported)) return { file, resized: null }; // no ganamos nada: se sube el original

    const name = String(file.name || "photo").replace(/\.[^.]+$/, "") + ".jpg";
    return {
      file: new File([blob], name, { type: "image/jpeg", lastModified: file.lastModified }),
      resized: { from_kb: Math.round(file.size / 1024), to_kb: Math.round(blob.size / 1024) },
    };
  } catch {
    return { file, resized: null };
  }
}
